import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "ghost"
  | "quiet"
  | "brand";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode | IconName;
  iconRight?: ReactNode | IconName;
  full?: boolean;
  active?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-text text-bg border border-text hover:opacity-90",
  secondary: "bg-elevated text-text border border-border-strong hover:bg-bg-2",
  danger: "bg-danger text-white border border-danger hover:opacity-90",
  ghost: "bg-transparent text-text border border-transparent hover:bg-bg-2",
  quiet: "bg-transparent text-text-2 border border-border hover:bg-bg-2",
  brand: "bg-primary text-white border border-primary hover:bg-primary-2",
};

const SIZES: Record<ButtonSize, string> = {
  xs: "h-6 px-2 text-[11px] gap-1 rounded",
  sm: "h-7 px-2.5 text-xs gap-1.5 rounded",
  md: "h-9 px-3.5 text-sm gap-2 rounded",
  lg: "h-10 px-4 text-sm gap-2 rounded-lg",
};

function resolveIcon(
  icon: ReactNode | IconName | undefined,
  size: number,
): ReactNode {
  if (!icon) return null;
  if (typeof icon === "string")
    return <Icon name={icon as IconName} size={size} />;
  return icon;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      loading,
      icon,
      iconRight,
      full,
      active,
      children,
      disabled,
      className = "",
      ...rest
    },
    ref,
  ) {
    const iSize =
      size === "xs" ? 12 : size === "sm" ? 13 : size === "lg" ? 16 : 15;
    const activeCls = active ? "ring-1 ring-text/40 bg-text text-bg" : "";
    return (
      <button
        {...rest}
        ref={ref}
        disabled={disabled || loading}
        className={`inline-flex items-center justify-center font-medium transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-bg ${VARIANTS[variant]} ${SIZES[size]} ${full ? "w-full" : ""} ${activeCls} ${className}`}
      >
        {loading ? (
          <>
            <Icon name="spinner" size={iSize} className="animate-spin" />
            <span className="sr-only">...</span>
          </>
        ) : (
          resolveIcon(icon, iSize)
        )}
        {children}
        {!loading && resolveIcon(iconRight, iSize)}
      </button>
    );
  },
);
